<script lang="ts">
    import {
        Table,
        TableBody,
        TableBodyCell,
        TableBodyRow,
        TableHead,
        TableHeadCell,
        Button,
        Modal, Fileupload, Input, Textarea, Heading, Tabs, TabItem, Select, Label
    } from 'flowbite-svelte';
    import {writable} from 'svelte/store';
    import {enhance} from "$app/forms";
    import {page} from "$app/stores";
    import type {Documents} from "$lib/types/documents";
    import dayjs from "dayjs";
    import OrderButton from "$items/OrderButton.svelte";
    import {TagSolid, ArrowsRepeatCountOutline, CirclePlusSolid} from "flowbite-svelte-icons";
    import type {DateType} from "$lib/types";
    import EmployeeUploadModal from "$lib/partials/employee/EmployeeUploadModal.svelte";
    import {invalidateAll} from "$app/navigation";
    import SortableTable from "$lib/components/SortableTable.svelte";
    import {onMount} from "svelte";
    import OpenButton from "$lib/components/ui/OpenButton.svelte";
    import ModalPreview from "$lib/partials/ui/ModalPreview.svelte";
    import {documentTags, tagToString} from "$lib/utils/documentTag";
    import CardLayout from "$items/CardLayout.svelte";
    import DocumentActions from "$lib/partials/employee/DocumentActions.svelte";
    import {listView} from "$lib/stores/viewStore";
    import ImageRenderer from "$lib/components/ui/ImageRenderer.svelte";
    import {globalConfirm} from "$lib/stores/modal";
    import {retire} from "$lib/api";
    import {deleteEmployeeImage} from "$lib/calls/employee";
    import {updateDocumentTag} from "$lib/calls/document";
    import EmployeeDocumentActionComponent from "$lib/partials/employee/EmployeeDocumentActionComponent.svelte";


    let documents = $page.data.documents as Documents;
    let normalizedDocuments: {
        imageTag: string;
        documentNumber?: string;
        name: string;
        location: string;
        id: number;
        createdAt: DateType
    }[] = [];


    let fotos = [];
    let downloadUrl: string;
    let downloadOpen = false;
    let currentId = 0;
    let open = false;

    let currentDocument = null;

    const docDownload = async (id: number) => {
        currentDocument = normalizedDocuments.find(x => x.id === id);

        currentId = id;
        downloadOpen = true;
        setTimeout(() => {
            const button = document.getElementById('download')
            button.href = currentDocument.location;
            button.download = currentDocument.name.replace(/\.[a-z]{2,4}/, '')
        })

        downloadUrl = currentDocument.location;
    }

    const header = [
        {
            property: 'name',
            label: 'Name',
            sortable: true,
            movable: true,
            render: (row: any) => decodeURIComponent(row.name)
        }, {
            property: 'location',
            label: 'Document',
            sortable: true,
            movable: true,
            component: ImageRenderer,
        }, {
            property: 'createdAt',
            label: 'Created At',
            sortable: true,
            movable: true,
            render: (row: any) => dayjs.unix(row.createdAt.stamp).format('DD.MM.YYYY'),
            customSort: (a, b, c) => a.createdAt.stamp > b.createdAt.stamp ? c : -1 * c
        }, {
            property: 'documentNumber',
            label: 'Number',
            sortable: true,
            movable: true,
            render: (row) => row.documentNumber || '-'
        }, {
            property: 'imageTag',
            label: 'Kategorie',
            sortable: true,
            movable: true,
            render: (row) => row.templateDocumentId ? 'Vertragsdokument' : tagToString(row.imageTag) || '-'
        }, {
            property: 'currentId',
            label: 'Aktion',
            component: EmployeeDocumentActionComponent,
            props: {
                findDocument: currentId => normalizedDocuments.find(x => x.currentId === currentId),
                employee: $page.data.employee,
                callback: (document, action) =>  {
                    if(action === 'removed') {
                        normalizedDocuments = normalizedDocuments.filter(x => x.currentId !== document.currentId)
                    } else {
                        normalizedDocuments = normalizedDocuments.map(x => x.currentId === document.currentId ? document : x)
                    }
                }
            },
        }
    ]

    $: accordions = [
        {
            title: 'Bilder | Porträt | Aufnahmen',
            categories: [
                {
                    name: 'Mitarbeiterbilder',
                    filtered: normalizedDocuments.filter(x => x.imageTag === 'picture-company')
                },
                {name: 'Mitarbeiterausweis', filtered: normalizedDocuments.filter(x => x.imageTag === 'employee-id')},
                {name: 'Sedcard', filtered: normalizedDocuments.filter(x => x.imageTag === 'sedcard')},
                {
                    name: 'Bewerbungsbilder',
                    filtered: normalizedDocuments.filter(x => x.imageTag === 'picture' || x.imageTag === 'avatar')
                },
            ]
        },
        {
            title: 'Urkunden | Bescheinigungen | Zertifikate',
            categories: [
                {name: 'Lizenzen', filtered: normalizedDocuments.filter(x => x.imageTag === 'license')},
                {
                    name: 'Studiennachweis',
                    filtered: normalizedDocuments.filter(x => x.imageTag === 'student-verification')
                },
            ]
        },
        {
            title: 'Vertragsdokumente',
            categories: [
                {name: 'Vertragsdokumente', filtered: normalizedDocuments.filter(x => x.imageTag === 'contract' || x.imageTag === 'contract-document')},
            ]
        },
        {
            title: 'Ausweisdokumente',
            categories: [
                {name: 'Ausweis | Reisepass', filtered: normalizedDocuments.filter(x => x.imageTag === 'id-card')},
            ]
        },
        {
            title: 'Sonstiges',
            categories: [
                {name: 'Unkategorisiert', filtered: normalizedDocuments.filter(x => x.imageTag === 'other')},
            ]
        }
    ]

    const addDocument = (ev: CustomEvent) => {
        console.log({ev})
        const img = {
            ...ev.detail,
            name: ev.detail.location.replace('https://recrew-employees.s3.eu-north-1.amazonaws.com/' + $page.data.employee.uuid, '~')
        }
        normalizedDocuments = [...normalizedDocuments, img]
    }

    const deleteDocument = async () => {
        await deleteEmployeeImage($page.data.employee.uuid, normalizedDocuments.find(x => x.id === currentId))
        normalizedDocuments = normalizedDocuments.filter(x => x.id !== currentId)
    }

    const updateDocument = async () => {
        await updateDocumentTag(
            normalizedDocuments.find(x => x.id === currentId),
            $page.data.employee.uuid,
            currentDocument.imageTag
        );
        downloadOpen = false;
        normalizedDocuments = normalizedDocuments.map(x => x.id === currentId ? {
            ...x,
            imageTag: currentDocument.imageTag
        } : x)
    }


    onMount(() => {
        normalizedDocuments = [
            ...$page.data.documents.contracts.map(x => ({...x, imageTag: 'contract', documentNumber: 'D-' + x.id})),
            ...$page.data.documents.images.map(x => ({
                ...x,
                name: x.location.replace('https://recrew-employees.s3.eu-north-1.amazonaws.com/' + $page.data.employee.uuid, '~')
            }))
        ]
        for(let i = 0; i < normalizedDocuments.length; i++) {
            normalizedDocuments[i].currentId = i;
        }
        console.log({normalizedDocuments})

    })
</script>

<svelte:head>
    <title>
        Dokumente | {$page.data.employee.name}
    </title>
</svelte:head>
{#if $listView}
    {#if normalizedDocuments.length > 0}
        <SortableTable data={normalizedDocuments} {header}/>
    {/if}
{:else}
    <div class="space-y-3">
        {#each accordions as accordion, i}
            <CardLayout title={accordion.title} accordion display={i===0}>
                <!--                <div class="shadow-sm border rounded-lg px-4 py-3 bg-white mb-5 space-y-2" >-->
                <div class="px-4 py-3 mb-5 space-y-2">
                    <div class="flex gap-3">
                        <div class="flex-1">
                            {#each accordion.categories as category}
                                <Heading class="text-neutral-600 mb-3 mt-2" tag="h4">{category.name}</Heading>
                                <SortableTable data={category.filtered} {header}>
                                    <svelte:fragment slot="empty">
                                        <div class="px-3 py-2 text-neutral-500 flex gap-2">No data to display</div>
                                    </svelte:fragment>
                                </SortableTable>
                            {/each}


                        </div>
                        <div class="w-44 border-l border-neutral-200 pl-3">
                            <DocumentActions {fotos} on:update={addDocument}/>
                        </div>
                    </div>
                </div>
            </CardLayout>
        {/each}

    </div>
{/if}


<!--<div class="flex items-center justify-between mb-4">
    <Heading class="text-neutral-600 mb-3 text-xl" tag="h2">Documents</Heading>
    <Button on:click={() => open = true}>Upload</Button>
</div>-->


<div class="w-full">


</div>
